import { FunctionComponent } from "preact";
import { useState, useEffect } from "preact/hooks";
import { getMonitoringService } from "../../../lib/monitoring";
import { useKismetConfig } from "../../../contexts/KismetContext";
import { CountdownTimer } from "./CountdownTimer";

interface OfferPreviewCardProps {
  headlineText: string;
  descriptionText: string;
  imageUrl: string;
  hotelName: string;
  hotelLogo: string;
  discountPercentage: number;
  expiresAt: string;
  offerCode?: string;
  onBookNow?: () => void;
  onSaveDeal?: () => void;
}

const OfferPreviewCard: FunctionComponent<OfferPreviewCardProps> = ({
  headlineText,
  descriptionText,
  imageUrl,
  hotelName,
  hotelLogo,
  discountPercentage,
  expiresAt,
  offerCode,
  onSaveDeal = () => {},
}) => {
  const [copied, setCopied] = useState(false);
  const kismetConfig = useKismetConfig();
  const monitoringService = getMonitoringService();

  // Track when the PUO is viewed (when component mounts)
  useEffect(() => {
    // Call the specific method for tracking PUO views
    monitoringService.onPuoView();
  }, []); // Empty dependency array means this runs once on mount

  const handleCopyCode = () => {
    if (offerCode) {
      navigator.clipboard.writeText(offerCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleBookNow = () => {
    // Call the monitoring service's tracking method
    monitoringService.onPuoClick();
    monitoringService.onBookNow();
    // Call the config's onBookNow if available
    kismetConfig.onBookNow?.();
  };

  return (
    <div className="offer-preview-card flex flex-col h-[600px] sm:h-[650px] md:h-[700px] lg:h-[750px] w-full rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
      {/* Hero Image */}
      <div className="relative w-full aspect-[16/9] flex-shrink-0 group overflow-hidden">
        <img
          src={imageUrl}
          alt={headlineText}
          className="w-full h-full object-cover m-0 transition-transform duration-500 ease-out group-hover:scale-110"
        />
        {discountPercentage > 0 && (
          <div className="absolute top-2 sm:top-3 md:top-4 right-2 sm:right-3 md:right-4 bg-red-500 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-semibold z-10">
            {discountPercentage}% OFF
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <CountdownTimer expiresAt={expiresAt} />
        </div>
      </div>

      {/* Content Container with Absolute Footer */}
      <div className="relative flex-1 min-h-0">
        {/* Scrollable Content */}
        <div className="h-full overflow-y-auto p-3 sm:p-4 md:p-5 lg:p-6 pb-[100px] sm:pb-[110px] md:pb-[120px]">
          {/* Offer Details */}
          <h3 className="font-bold text-xl mb-3 text-gray-900 m-0">
            {headlineText}
          </h3>
          <p className="text-base text-gray-600 mb-3">{descriptionText}</p>
        </div>

        {/* Absolutely Positioned Footer */}
        <div className="offer-preview-card-footer absolute bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-3 sm:p-4 md:p-5">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={onSaveDeal}
              className="bg-gray-100 text-gray-800 py-2 px-4 rounded-l-lg text-base font-semibold hover:bg-gray-200 transition-colors border border-gray-200"
            >
              Save for Later
            </button>
            <button
              onClick={handleBookNow}
              className="bg-black text-white py-2 px-4 rounded-r-lg text-base font-semibold hover:bg-gray-800 transition-colors"
            >
              Book Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferPreviewCard;
